/**
 * The adversaries reading route's typed edge (`GET .../reading/adversaries`), written field
 * for field from the dispatch brief's contract rather than imported: `api` is not landed yet.
 *
 * `null` on a numeric field means "not measured", never zero -- the same convention
 * `../../accounts/api/schemas.ts`'s reading routes use, so this file renders every absence the
 * same way `../../accounts/account/KpiRow.tsx` does.
 */

import { z } from 'zod';

export const trackedAccountAlignmentSchema = z.enum(['own', 'adversary']);
export type TrackedAccountAlignment = z.infer<typeof trackedAccountAlignmentSchema>;

const dailyReachSchema = z.object({ day: z.string(), value: z.number() });

export const adversaryAccountRowSchema = z.object({
  tracked_account_id: z.uuid(),
  handle: z.string().nullable(),
  label: z.string().nullable(),
  alignment: trackedAccountAlignmentSchema,
  posts: z.number().int(),
  reach: z.number().nullable(),
  engagement: z.number().nullable(),
  targeting: z.number().int(),
  comments_read: z.number().int(),
  sentiment: z.number().nullable(),
  reach_days: z.array(dailyReachSchema),
  // The account's own responsible candidate (`.../candidate`); `null` when not set, defaulted
  // rather than required while `api`'s deploy lags this field's rollout (a candidate pin can
  // land on `core`/`web` before the api that serves it is unblocked and pushed).
  candidate_name: z.string().nullable().default(null),
});
export type AdversaryAccountRow = z.infer<typeof adversaryAccountRowSchema>;

// The responsible candidate an adversary account is marked with, inside one campaign
// (`.../tracked-accounts/{id}/candidate`). Mirrors `../../record/api/schemas.ts`'s
// `allianceSchema`'s TSE-or-entity pair rather than importing it: that schema also carries
// alliance-only fields (`relation`, `source`, `kind`) this one has no use for.
export const candidateSchema = z.object({
  named_entity_id: z.uuid().nullable(),
  tse_election_id: z.uuid().nullable(),
  tse_registration_id: z.string().nullable(),
  name: z.string().nullable(),
  photo_url: z.string().nullable(),
});
export type Candidate = z.infer<typeof candidateSchema>;

// All three `null` clears the account's responsible candidate ("Nenhum / não se aplica").
export interface CandidateWrite {
  named_entity_id: string | null;
  tse_election_id: string | null;
  tse_registration_id: string | null;
}

export const candidateGroupSchema = z.object({
  key: z.string(),
  candidate: candidateSchema.nullable(),
  accounts: z.array(adversaryAccountRowSchema),
  posts: z.number().int(),
  reach: z.number().nullable(),
  engagement: z.number().nullable(),
  targeting: z.number().int(),
});
export type CandidateGroup = z.infer<typeof candidateGroupSchema>;

export const adversariesReadingSchema = z.object({
  start: z.string(),
  end: z.string(),
  // Own accounts first, then adversaries by reach -- the order `api` returns, never resorted.
  accounts: z.array(adversaryAccountRowSchema),
  // Adversary accounts only, grouped by responsible candidate, "unassigned" last. Defaulted to
  // `[]` for the same rollout-ordering reason as `candidate_name` above.
  adversary_groups: z.array(candidateGroupSchema).default([]),
});
export type AdversariesReading = z.infer<typeof adversariesReadingSchema>;

export const ANALYSIS_SCOPES = ['ALL', 'RELATED'] as const;
export const analysisScopeSchema = z.enum(ANALYSIS_SCOPES);
export type AnalysisScope = z.infer<typeof analysisScopeSchema>;

export const analysisScopeStateSchema = z.object({
  tracked_account_id: z.uuid(),
  // `null` inherits from the account's alignment.
  analysis_scope: analysisScopeSchema.nullable(),
  effective: analysisScopeSchema,
});
export type AnalysisScopeState = z.infer<typeof analysisScopeStateSchema>;

export interface AnalysisScopeWrite {
  analysis_scope: AnalysisScope | null;
}
